import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantData from '@data';
import tenantUtils from '@utils';
import { Typography } from 'antd';
import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AddAmenities, ImageSelect, VideoSelect } from '..';
import { useScrollToPageSection } from '../../../hooks';
import { Flex, Group, Icon, Number, Popover, RadioButtons, Select, TextInput } from '../../common';
import { IconStyled } from '../../common/icon/IconStyled';
import CreditInfo from '../../credits-info/credits-info';
import MobileVerification from '../../mobile-number-verification/mobile-verification';
import PostListingSkeleton from './post-listing-from-skeleton';
import { PriceCheckValue } from '../../../tenant/common/components/post-listing/styled';
import { imageUploadSuccessEvent } from '../../../services/analyticsService';
import appConstants from '@constants';
const { Text } = Typography;

const PostListingForm = (props) => {
  const {
    loading,
    formik,
    isMobile,
    setLoading,
    users = [],
    listingPurposeId,
    areaMaxLength,
    tooltipText,
    listingType,
    bedroomList,
    bathRoomsList,
    isCreditsAvailable,
    clearUserSelections = () => {},
    getLocationsLatLong = () => {},
    nationalAddressLoading,
    alreadyPosted = false,
    nationalAddress = false,
    furnished = false,
    saleType = false,
    additionalNumber = false,
    mobileVerification = false,
    saleTypes,
    isOffPlan = false,
    isDailyRental = false,
    purposeField = false,
    areaField = false,
    packageInfo = false,
    completion_status,
    setAutoGenerateContent,
    autoGenerateContent,
  } = props;
  const { user } = useSelector((state) => state.app.loginUser);
  const { locale } = useSelector((state) => state?.app?.AppConfig);
  const { t } = useTranslation();
  const OFFPLAN_PURPOSE_ID = 5;
  const showVideoField = appConstants.SHOW_VIDEO_FIELD
  const propertyTypeButtonList = useMemo(() => {
    const propertyTypeobject = tenantData.listingTypes().reduce((acc, data) => {
      acc[data.id] = data.sub_types.map((subType) => ({
        ...subType,
        key: subType.id,
        label: tenantUtils.getLocalisedString(subType, 'title'),
      }));
      return acc;
    }, {});
    return propertyTypeobject;
  }, []);
  useEffect(() => {
    if (tenantConstants?.AUTO_TRANSLATE_CONTENT) {
      setAutoGenerateContent((prev) => ({
        ...prev,
        title: formik.values?.generate_title,
        description: formik.values?.generate_description,
      }));
    }
  }, [
    formik.values?.generate_title,
    formik.values?.generate_description,
    locale,
    tenantConstants?.AUTO_TRANSLATE_CONTENT,
  ]);
  useScrollToPageSection();

  const renderInputPopOver = () => {
    return (
      <Popover
        placement="top"
        content={<div style={{ maxWidth: '300px', width: '100%' }}>{tooltipText}</div>}
        action="hover"
      >
        <>
          <Icon icon="AiOutlineInfoCircle" iconProps={{ color: '#222' }} />
        </>
      </Popover>
    );
  };
  const completionStatusIntialValue = useMemo(() => {
    return completion_status !== 'mixed';
  }, [completion_status]);
  return (
    <>
      <PostListingSkeleton icon="PropertyInformationIcon" title={t('Property Information')}>
        <Group gap="32px" style={{ maxWidth: 640 }}>
          {purposeField && (
            <RadioButtons
              name="purpose"
              shape="round"
              key="purpose"
              label={t('Select Purpose')}
              labelIcon="MdOutlineCheckCircle"
              valueKey="id"
              buttonList={tenantData.purposeList}
              value={formik?.values?.['purpose']}
              handleChange={(e) => {
                clearUserSelections();
                formik.setFieldValue('purpose', e?.target?.value, true);
              }}
              disabled={alreadyPosted}
              errorMsg={formik?.errors?.['purpose'] && formik?.touched?.['purpose'] && formik?.errors?.['purpose']}
            />
          )}
          <RadioButtons
            name="property_type"
            key="property_type"
            shape="round"
            label={t('Select Property Type')}
            labelIcon="BiBuildingHouse"
            value={formik?.values['property_type']}
            handleChange={(e) => {
              clearUserSelections();
              formik.setFieldValue('property_type', e?.target?.value, true);
              const selectedPropertyType = propertyTypeButtonList[
                isOffPlan ? OFFPLAN_PURPOSE_ID : listingPurposeId || formik?.values?.['purpose']
              ]?.find((data) => data?.id === e?.target?.value);
              formik.setFieldValue('property_type_label', selectedPropertyType?.title?.en);
            }}
            errorMsg={
              formik.errors['property_type'] && formik.touched['property_type'] && formik.errors['property_type']
            }
            buttonList={
              listingPurposeId
                ? propertyTypeButtonList[isOffPlan ? OFFPLAN_PURPOSE_ID : listingPurposeId]
                : propertyTypeButtonList?.[formik?.values?.['purpose']]
            }
            disabled={alreadyPosted}
          />
          {formik?.values?.property_type && (
            <>
              {!tenantData.skipFieldsForField?.['bedrooms']?.property_type[formik.values['property_type']]
                ?.skipField && (
                <RadioButtons
                  name="bedrooms"
                  shape="round"
                  key="bedrooms"
                  label={t('Number of Bedrooms')}
                  inputLeftIcon="user"
                  labelIcon="LuBedDouble"
                  value={formik?.values['bedrooms']}
                  handleChange={(e) => {
                    formik.setFieldValue('bedrooms', e?.target?.value, true);
                  }}
                  buttonList={bedroomList}
                  errorMsg={formik.errors['bedrooms'] && formik?.touched['bedrooms'] && formik.errors['bedrooms']}
                />
              )}

              {!tenantData.skipFieldsForField?.['bathrooms']?.property_type[formik.values['property_type']]
                ?.skipField && (
                <RadioButtons
                  name="bathrooms"
                  shape="round"
                  key="bathrooms"
                  label={t('Number of Bathrooms')}
                  inputLeftIcon="user"
                  labelIcon="BiBath"
                  value={formik?.values['bathrooms']}
                  handleChange={(e) => {
                    formik.setFieldValue('bathrooms', e?.target?.value, true);
                  }}
                  buttonList={bathRoomsList}
                  errorMsg={formik.errors['bathrooms'] && formik?.touched['bathrooms'] && formik.errors['bathrooms']}
                />
              )}

              {furnished &&
                !TenantComponents.skipFieldsForField?.['furnished']?.property_type[formik.values['property_type']]
                  ?.skipField && (
                  <RadioButtons
                    name="furnished"
                    shape="round"
                    key="furnished"
                    label={t('Furnished')}
                    inputLeftIcon="user"
                    labelIcon="LuLamp"
                    value={formik?.values['furnished']}
                    handleChange={(e) => {
                      formik.setFieldValue('furnished', e?.target?.value, true);
                    }}
                    buttonList={[
                      { key: '1', label: t('Yes') },
                      { key: '0', label: t('No') },
                    ]}
                    errorMsg={formik.errors['furnished'] && formik?.touched['furnished'] && formik.errors['furnished']}
                  />
                )}
            </>
          )}

          {areaField && (
            <TextInput //ToDo use TextInput With Unit
              key="area"
              name="area"
              type="number"
              label={t('Area Size')}
              labelIcon="IconAreaSize"
              placeholder={t('e.g., 800')}
              useInternalState
              handleChange={(event) => {
                formik.setFieldValue('area', event.target.value, true);
              }}
              suffix={<span className="color-gray-dark">{t(tenantData.areaUnitList[0]?.title_short)}</span>}
              handleBlur={(event) => {
                formik.setFieldTouched('area', true);
                formik.setFieldValue('area', event.target.value, true);
              }}
              maxLength={areaMaxLength}
              value={formik?.values['area']}
              skeletonLoading={loading}
              errorMsg={formik?.errors['area'] && formik?.touched['area'] && formik?.errors['area']}
            />
          )}
          {completion_status && (
            <RadioButtons
              name="completion_status"
              shape="round"
              key="completion_status"
              label={t('Completion Status')}
              inputLeftIcon="user"
              labelIcon="IoMdCheckmarkCircleOutline"
              value={formik.values.completion_status}
              handleChange={(e) => {
                completion_status === 'mixed' && formik.setFieldValue('completion_status', e?.target?.value, true);
              }}
              buttonList={[
                { key: 'off_plan', label: t('Off-Plan') },
                { key: 'ready', label: t('Ready') },
              ]}
              errorMsg={
                formik.errors['completion_status'] &&
                formik?.touched['completion_status'] &&
                formik.errors['completion_status']
              }
              disabled={completionStatusIntialValue}
            />
          )}
          {saleType && formik?.values?.completion_status == 'off_plan' && (
            <Select
              name="sale_type"
              label={t('Sale Type')}
              labelIcon="BiBuildingHouse"
              value={formik?.values['sale_type']}
              onChange={(value) => formik.setFieldValue('sale_type', value)}
              errorMsg={formik.errors['sale_type'] && formik?.touched['sale_type'] && formik.errors['sale_type']}
              placeholder={t('Select Sale Type')}
              options={saleTypes}
              getOptionValue={(e) => e.id}
              getOptionLabel={(e) => t(e.name)}
              allowClear
            />
          )}
        </Group>
      </PostListingSkeleton>
      <PostListingSkeleton icon="IconLocationPurpose" title={t('Property Location')}>
        <Group gap="32px" style={{ maxWidth: 640 }}>
          {tenantConstants.SHOW_NATIONAL_ADDRESS && nationalAddress && (
            <TextInput
              key="national_address"
              name="national_address"
              label={t('National Address')}
              maxLength={8}
              labelIcon="IconNavigate"
              placeholder={t('e.g.,RRRR2929')}
              handleChange={(event) => {
                formik.setFieldValue('national_address', event.target.value, true);
              }}
              handleBlur={() => {
                formik.setFieldTouched('national_address', true);
                if (formik?.values['national_address'] && !formik?.errors['national_address']) {
                  getLocationsLatLong(formik?.values['national_address']);
                }
              }}
              renderPopover={() => renderInputPopOver()}
              value={formik?.values['national_address']}
              skeletonLoading={loading}
              errorMsg={
                formik?.errors['national_address'] &&
                formik?.touched['national_address'] &&
                formik?.errors['national_address']
              }
              loading={nationalAddressLoading}
            />
          )}
          <div id="location">
            <TenantComponents.LocationSelect
              name="Location_select"
              formik={formik}
              setFieldValue={formik.setFieldValue}
              setFieldTouched={formik.setFieldTouched}
              value={formik?.values?.['location-info']}
              error={formik.errors?.['location-info']}
              touched={formik.touched?.['location-info']}
              onBlur={formik.handleBlur}
              item={{ key: 'location-info' }}
              onCityChange={(option) => {}}
              disabled={{
                city: formik?.values?.location?.city_id,
              }}
              renderCrossCityAlerts={() => {}}
              hideLocation={false}
              onLocationSelect={() => {}}
              user={user}
              showPlot={true}
            />
          </div>
          {additionalNumber && (
            <TextInput
              key="additional_number"
              name="additional_number"
              type="input"
              labelIcon={'MdOutlineMap'}
              placeholder={t('Enter additional number')}
              value={formik?.values['additional_number']}
              label={t('Additional / Unit Number')}
              errorMsg={
                formik?.errors['additional_number'] &&
                formik?.touched['additional_number'] &&
                formik?.errors['additional_number']
              }
              onChange={(e) => {
                formik.setFieldValue('additional_number', e?.target?.value);
              }}
            />
          )}
        </Group>
      </PostListingSkeleton>
      <PostListingSkeleton icon="IconImagesPost" title={t('Property Images')}>
        <Group id="images" gap="32px" style={{ maxWidth: 640 }}>
          <ImageSelect
            labelIcon="LuImage"
            label={t('Images')}
            values={formik.values}
            setFieldValue={formik.setFieldValue}
            errorMsg={
              formik.errors['property_images'] && formik.touched['property_images'] && formik.errors['property_images']
            }
            valueKey={'property_images'}
            filesAllowed={tenantData.allowedFilesType}
            imageBankIcon={false}
            showImageBank={false}
            onImageUpload={(response) => {
              imageUploadSuccessEvent(user, response, !alreadyPosted, formik.values);
            }}
            showQualityTip={tenantConstants.SHOW_QUALITY_TIP}
            propertyTypeId={formik.values?.['property_type']}
          />

          {showVideoField && (
            <VideoSelect
              labelIcon="MdVideoCall"
              label={t('Add Videos of your Property')}
              value={formik.values['videos']}
              setFieldValue={formik.setFieldValue}
              errorMsg={formik.errors['videos'] && formik.touched['videos'] && formik.errors['videos']}
              valueKey="videos"
              hostList={tenantData.videoHostsList}
              isMobile={isMobile}
            />
          )}
        </Group>
      </PostListingSkeleton>
      <PostListingSkeleton icon="IconAdInformation" title={t('Price & Features')}>
        <Group id="specs" gap="32px" style={{ maxWidth: 640 }}>
          {formik?.values?.listing_purpose?.slug == 'sale' || isDailyRental || formik.values.purpose == 1 ? (
            <TextInput
              key="price"
              name="price"
              label={t('Price') + ' ' + `${isDailyRental ? ` (${listingType})` : ''}`}
              labelIcon="RiPriceTag3Line"
              useInternalState
              placeholder={t('Enter Price')}
              type="number"
              // onChange={(event) => {
              //   formik.setFieldValue('price', event.target.value, true);
              // }}
              handleChange={(event) => {
                formik.setFieldValue('price', event.target.value, true);
              }}
              suffix={<span className="color-gray-dark">{tenantConstants.CURRENCY_SYMBOL()}</span>}
              handleBlur={(event) => {
                formik.setFieldValue('price', event.target.value, true);
              }}
              value={formik?.values['price']}
              skeletonLoading={loading}
              errorMsg={formik?.errors['price'] && formik?.touched['price'] && formik?.errors['price']}
              extra={() => {
                return formik?.values['price'] && !formik?.errors['price'] ? (
                  <PriceCheckValue>
                    <Number
                      type="price"
                      className={'text-muted'}
                      value={formik?.values['price']}
                      tooltip={false}
                      compact={false}
                    />
                  </PriceCheckValue>
                ) : null;
              }}
            />
          ) : (
            <>
              <RadioButtons
                name="rental_frequency"
                shape="round"
                key="rental_frequency"
                label={t('Rental Frequency')}
                inputLeftIcon="user"
                labelIcon="MdOutlineMap"
                value={formik?.values['rental_frequency']}
                handleChange={(e) => {
                  formik.setFieldValue('rental_frequency', e?.target?.value, true);
                }}
                buttonList={tenantData.rentalFrequencyList.map((item) => ({ ...item, label: t(item.label) }))}
                errorMsg={
                  formik.errors['rental_frequency'] &&
                  formik?.touched['rental_frequency'] &&
                  formik.errors['rental_frequency']
                }
              />
              <TextInput
                key="rental_price"
                name="rental_price"
                label={
                  <>
                    {t('Rent Price')} <>({tenantConstants.CURRENCY_SYMBOL()})</>
                  </>
                }
                labelIcon="RiCalendar2Fill"
                placeholder={t('Enter Rent')}
                type="number"
                // handleBlur={(event) => {
                //   formik.setFieldValue('rental_price', event.target.value, true);
                // }}
                onChange={(event) => {
                  formik.setFieldValue('rental_price', event.target.value, true);
                }}
                handleChange={(event) => {
                  formik.setFieldValue('rental_price', event.target.value, true);
                }}
                value={formik?.values['rental_price']}
                skeletonLoading={loading}
                errorMsg={
                  formik?.errors['rental_price'] && formik?.touched['rental_price'] && formik?.errors['rental_price']
                }
                suffix={<span className="color-gray-dark">{tenantConstants.CURRENCY_SYMBOL()}</span>}
                extra={() => {
                  return formik?.values['rental_price'] && !formik?.errors['rental_price'] ? (
                    <PriceCheckValue>
                      <Number
                        type="price"
                        value={formik?.values['rental_price']}
                        tooltip={false}
                        compact={false}
                        className={'text-muted'}
                      />
                    </PriceCheckValue>
                  ) : null;
                }}
              />
            </>
          )}
          <div id="amenities">
            {!tenantData.skipFieldsForField?.['feature_and_amenities']?.property_type[
              formik.values['sub_listing_type']?.id || formik.values['property_type']
            ]?.skipField &&
              formik?.values?.property_type && (
                <AddAmenities
                  name="feature_and_amenities"
                  handleChange={(values) => {
                    formik.setFieldValue('feature_and_amenities', values);
                  }}
                  handleBlur={formik?.handleBlur}
                  value={formik?.values['feature_and_amenities']}
                  skeletonLoading={loading}
                  key="feature_and_amenities"
                  labelIcon="MdOutlineOtherHouses"
                  bodyStyle={{ paddingBlockStart: 'initial' }}
                  payloadKey={formik.values?.['property_type']}
                  purposeId={listingPurposeId}
                  showQualityTip={tenantConstants.SHOW_QUALITY_TIP}
                  propertyTypeId={formik.values?.['property_type']}
                />
              )}
          </div>
          <div id="description">
            <div style={{ marginTop: 24 }}>
              <TenantComponents.GenerateContentField
                formik={formik}
                setSubmitDisable={setLoading}
                labelIcon="PiTextT"
                desc={'title'}
                skipField={locale === 'en' ? 'property_description_en' : 'property_description_ar'}
                name={locale === 'ar' ? 'property_title_ar' : 'property_title_en'}
                dir={locale === 'ar' ? 'rtl' : ''}
                label={locale === 'ar' ? 'عنوان' : 'Title'}
                placeholder={
                  locale === 'ar'
                    ? 'أدخل العنوان، على سبيل المثال، منزل جديد جميل...'
                    : 'Enter title e.g Beautiful new house...'
                }
                payloadKey={locale === 'en' ? 'title' : 'title_ar'}
                disabled={!isDailyRental && !(formik?.values?.property_type && formik?.values?.purpose)}
                showAutoSwitch={true}
              />
            </div>
            {tenantConstants?.AUTO_TRANSLATE_CONTENT && !autoGenerateContent?.title && (
              <div
                style={{
                  marginInlineEnd: 49,
                  marginTop: 24,
                  direction: locale === 'en' ? 'rtl' : 'ltr',
                  textAlign: locale === 'en' ? 'right' : 'left',
                }}
              >
                <TenantComponents.GenerateContentField
                  formik={formik}
                  setSubmitDisable={setLoading}
                  lineCount={1}
                  desc={'title'}
                  skipField={locale === 'en' ? 'property_description_en' : 'property_description_ar'}
                  name={locale === 'en' ? 'property_title_ar' : 'property_title_en'}
                  dir={locale == 'en' ? 'rtl' : ''}
                  label={locale === 'en' ? 'عنوان' : 'Title'}
                  placeholder={
                    locale === 'en'
                      ? 'أدخل العنوان، على سبيل المثال، منزل جديد جميل...'
                      : 'Enter title e.g Beautiful new house...'
                  }
                  payloadKey={locale === 'en' ? 'title_ar' : 'title'}
                  disabled={!isDailyRental && !(formik?.values?.property_type && formik?.values?.purpose)}
                />
              </div>
            )}
            <div style={{ marginTop: 24 }}>
              <TenantComponents.GenerateContentField
                formik={formik}
                setSubmitDisable={setLoading}
                labelIcon="BiDetail"
                lineCount={isMobile ? 3 : 5}
                limit={2500}
                desc={'description'}
                skipField={locale == 'en' ? 'property_title_en' : 'property_title_ar'}
                payloadKey={locale == 'en' ? 'description' : 'description_ar'}
                name={locale == 'ar' ? 'property_description_ar' : 'property_description_en'}
                dir={locale == 'ar' ? 'rtl' : ''}
                label={locale == 'ar' ? 'وصف' : 'Description'}
                placeholder={locale == 'ar' ? 'صف عقارك بالتفصيل' : 'Describe your property in detail'}
                disabled={!isDailyRental && !(formik?.values?.property_type && formik?.values?.purpose)}
                showAutoSwitch={true}
              />
            </div>
            {tenantConstants?.AUTO_TRANSLATE_CONTENT && !autoGenerateContent?.description && (
              <div
                style={{
                  marginInlineEnd: 49,
                  marginTop: 24,
                  direction: locale === 'en' ? 'rtl' : 'ltr',
                  textAlign: locale === 'en' ? 'right' : 'left',
                }}
              >
                <TenantComponents.GenerateContentField
                  formik={formik}
                  setSubmitDisable={setLoading}
                  lineCount={isMobile ? 3 : 5}
                  limit={2500}
                  desc={'description'}
                  skipField={locale == 'ar' ? 'property_title_en' : 'property_title_ar'}
                  payloadKey={locale == 'ar' ? 'description' : 'description_ar'}
                  name={locale == 'en' ? 'property_description_ar' : 'property_description_en'}
                  dir={locale == 'en' ? 'rtl' : ''}
                  label={locale == 'en' ? 'وصف' : 'Description'}
                  placeholder={locale == 'en' ? 'صف عقارك بالتفصيل' : 'Describe your property in detail'}
                  disabled={!isDailyRental && !(formik?.values?.property_type && formik?.values?.purpose)}
                />
              </div>
            )}

            {tenantConstants.DAILY_RENTAL_REDIRECTION && user?.is_daily_rental_redirection_enabled && (
              <TextInput
                key="redirection_link"
                name="redirection_link"
                label={t('Booking Link')}
                labelIcon="FaLink"
                placeholder={t('Enter Booking Link')}
                handleChange={(event) => {
                  formik.setFieldValue('redirection_link', event.target.value, true);
                }}
                handleBlur={() => {
                  formik.setFieldTouched('redirection_link', true);
                }}
                value={formik?.values['redirection_link']}
                skeletonLoading={loading}
                errorMsg={
                  formik?.errors['redirection_link'] &&
                  formik?.touched['redirection_link'] &&
                  formik?.errors['redirection_link']
                }
              />
            )}
          </div>
        </Group>
      </PostListingSkeleton>

      <PostListingSkeleton icon="IconContactInfo" title={t('Contact Information')}>
        <Group gap="32px" style={{ maxWidth: 640 }}>
          {user?.is_agency_admin && (
            <Select
              name="posting_as"
              key="posting_as"
              label={t('Posting As')}
              labelIcon="FiUser"
              placeholder={t('Select User')}
              value={tenantUtils.getLocalisedString(formik?.values['posting_as'], 'name')}
              onBlur={() => formik?.setFieldTouched('posting_as', true)}
              options={users}
              getOptionLabel={(op) => tenantUtils.getLocalisedString(op, 'name')}
              getOptionValue={(op) => op.id}
              disabled={true}
            />
          )}

          <Group template="max-content auto" gap="16px">
            <MobileVerification
              name={`mobile`}
              placeholder={t('Enter Phone Number')}
              defaultCountry={tenantConstants?.COUNTRY_CODE}
              value={formik?.values['mobile']}
              onChange={(value) => {
                formik.setFieldValue(`mobile`, !!value ? value : '', true);
              }}
              errorMsg={formik.errors['mobile'] && formik.touched['mobile'] && formik.errors['mobile']}
              touched={formik.touched.mobile}
              onBlur={() => formik.setFieldTouched(`mobile`, true)}
              label={t('Phone Number')}
              labelIcon={'MdSmartphone'}
              isMobile={isMobile}
              userType={'User'}
              isUserVerified={!!user?.is_mobile_verified}
              disabled={user?.mobile && !!user?.is_mobile_verified}
              containerClassName={'pos-rel'}
              userId={user?.id}
              countrySelectProps={{ disabled: true }}
            />
          </Group>
        </Group>
      </PostListingSkeleton>

      {isCreditsAvailable && packageInfo && (
        <PostListingSkeleton icon="IconPackageCredit" title={t('Package & Credits Info')}>
          {isMobile && (
            <>
              <Flex gap="16px" align="start">
                <IconStyled>
                  <Icon icon="IconCredit" />
                </IconStyled>
                <Flex vertical gap="8px" className="w-100">
                  <Text strong>{t('Package & Credits Info')}</Text>
                </Flex>
              </Flex>
            </>
          )}
          <CreditInfo
            isMobile={isMobile}
            loading={loading}
            cardStyle={{ marginInlineStart: '48px', width: !isMobile && '594px', borderWidth: 1 }}
          />
        </PostListingSkeleton>
      )}
    </>
  );
};

export default PostListingForm;
